'use client';

import type { HTMLAttributes } from 'react';
import { css } from 'styled-components';
import customCss from '@/app/components/hotel-detail/css/customCss.ts';
import { useIsDesktopOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR.ts';

/**
 * Ad slot containers of the hotel detail page. Their ids are the ones biff fills, see
 * `HOTEL_DETAIL_POSITIONS` in biffHotelDetailPage.ts.
 */

type AdslotProps = {
  /** the `area` of the containing `css grid`, where it should be added. Just a shortcut to avoid extra style prop. */
  gridArea?: string;
  /** the `id` as expected by tag manager */
  adId: string;
  marginTop?: number;
  sticky?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export function Skyscrappers(props: HTMLAttributes<HTMLDivElement>) {
  const isDesktopOrGreater = useIsDesktopOrGreater();
  return isDesktopOrGreater ? (
    <div {...props}>
      <Skyscraper adId="div-gpt-ad-external_right2" />
      <Skyscraper adId="div-gpt-ad-external_right3" marginTop={16} sticky={true} />
    </div>
  ) : null;
}

/**
 * Skyscraper sized ad. Keeps its content sticky at the top if there is more vertical room than what the ad needs.
 */
function Skyscraper(props: AdslotProps) {
  const { adId, marginTop, sticky = false, ...rest } = props;
  return (
    <div
      css={css`
        display: none;
        @media ${customCss.breakpoints.xl} {
          display: initial;
          padding: 16px 12px;
        }
      `}
      {...rest}
    >
      <div
        id={adId}
        css={css`
          display: flex;
          justify-content: flex-end;
          margin-right: 0.75rem;
          min-height: 600px;
          ${
            sticky
              ? `
          position: sticky;
          top: ${marginTop ? `${marginTop}px` : '1rem'};
          `
              : undefined
          }
        `}
      />
    </div>
  );
}

/**
 * LeaderBoard sized ad
 */
export function LeaderBoard(props: AdslotProps) {
  const { gridArea, adId, ...rest } = props;
  return (
    <div
      id={adId}
      css={css`
        max-width: 45.5rem;
        width: 100%;
        margin: 0 auto;
        grid-area: ${gridArea};
        display: flex;
        flex-direction: column;
        align-items: center;
      `}
      {...rest}
    />
  );
}
